import React from "react";
import type { DynamicTableLayout } from "../../../../../types";
import {
  detachDynamicFurniture,
  editDynamicFurniture,
  layoutDynamicTable
} from "../../../../../utils/dynamicFurniture";
import { PanelSection } from "../../../../ui/PanelShell";
import { useMapEditorActions } from "../../../contexts/MapEditorContext";
import type { ShapeSectionOptions } from "./ShapePanel";

export const DynamicFurniturePanel: React.FC<ShapeSectionOptions> = ({
  shapes
}) => {
  const { updateShape, setShapes, setSelectedIds } = useMapEditorActions();
  const panelId = React.useId();
  const shape = shapes[0];
  if (shape.type !== "dynamic-furniture") return null;
  const p = shape.layout;
  const update = (patch: Partial<DynamicTableLayout>) => {
    for (const s of shapes)
      if (s.type === "dynamic-furniture")
        updateShape(s.id, editDynamicFurniture(s, patch));
  };
  const number = (
    label: string,
    key: keyof DynamicTableLayout,
    min: number,
    max: number
  ) => (
    <label className="flex flex-col gap-1 text-xs">
      {label}
      <input
        className="w-full rounded border p-1"
        type="number"
        id={`${panelId}-${key}`}
        aria-label={label}
        min={min}
        max={max}
        step={key === "chairScale" ? 0.05 : 1}
        value={p[key] as number}
        onChange={(e) => {
          if (e.target.value !== "") update({ [key]: Number(e.target.value) });
        }}
      />
    </label>
  );
  return (
    <PanelSection label="Table set">
      <div className="flex flex-col gap-3">
        <label className="text-xs">
          Table shape
          <select
            className="w-full rounded border p-1"
            aria-label="Table shape"
            value={p.shape}
            onChange={(e) =>
              update({ shape: e.target.value as DynamicTableLayout["shape"] })
            }
          >
            <option value="round">Round</option>
            <option value="rectangle">Rectangle</option>
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          {number(
            p.shape === "round" ? "Diameter" : "Table width",
            "tableWidth",
            4,
            4000
          )}
          {p.shape === "rectangle" &&
            number("Table depth", "tableHeight", 4, 4000)}
        </div>
        {p.shape === "round" ? (
          number("Chairs", "chairs", 0, 24)
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {number("Top side", "top", 0, 24)}
            {number("Bottom side", "bottom", 0, 24)}
            {number("Left end", "left", 0, 24)}
            {number("Right end", "right", 0, 24)}
          </div>
        )}
        <label className="text-xs">
          Chair style
          <select
            className="w-full rounded border p-1"
            aria-label="Chair style"
            value={p.chairStyle}
            onChange={(e) =>
              update({
                chairStyle: e.target.value as DynamicTableLayout["chairStyle"]
              })
            }
          >
            <option value="standard">Standard</option>
            <option value="large">Large, with arms</option>
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          {number("Chair size scale", "chairScale", 0.05, 10)}
          {number("Chair gap", "gap", 0, 100)}
          {p.shape === "round"
            ? number("Seat offset (degrees)", "startAngle", -360, 360)
            : number("Corner radius", "cornerRadius", 0, 200)}
        </div>
        {layoutDynamicTable(p).crowded && (
          <p role="status" className="text-amber-700 text-xs">
            Seats are crowded. Enlarge the table or reduce the chair count.
          </p>
        )}
        <button
          className="rounded border p-2 text-xs"
          type="button"
          onClick={() => {
            const ids = new Set(shapes.map((s) => s.id));
            setShapes((all) =>
              all.map((s) =>
                ids.has(s.id) && s.type === "dynamic-furniture"
                  ? detachDynamicFurniture(s)
                  : s
              )
            );
            setSelectedIds([...ids]);
          }}
        >
          Detach into editable shapes
        </button>
      </div>
    </PanelSection>
  );
};
